import { createAction, createSelector, props } from '@ngrx/store';
import { DataBag } from '../store/reducer';
import { Library, Project } from './project.interface';
import { ProjectState } from './project.reducer';
import { myUserId } from '../user/user.selector';
import { RoleTypes } from '../organisation/role.interface';
import { ResourceData } from '../elements/resource/resource.types';
import { selectOrganisationFeature } from '../organisation/organisation.selector';
import { GeneralShapeDescriptor } from '../elements/resource/types/shape.type';
import { IRIToId } from '../util/iri';
import { selectProjectFeature } from './project.feature';

const asc = (v1, v2) => (v1 > v2 ? 1 : -1);

export const projectList = createSelector(
  selectProjectFeature,
  ({ allProjects }): Project[] => Object.values(allProjects),
);

export const selectFirstScene = createSelector(
  selectProjectFeature,
  ({ files }): ResourceData =>
    Object.values(files || {})
      .filter(file => !file.relationships?.childOf)
      .sort((f1, f2) => f1.literals.sceneIndex - f2.literals.sceneIndex)[0],
);

export const selectAllProjects = createSelector(
  selectProjectFeature,
  ({ allProjects }) => allProjects,
);

export const projectListLoading = createSelector(
  selectProjectFeature,
  ({ listLoading }): boolean => listLoading,
);

export const selectTakenVariables = (type: keyof Library, varName: string) =>
  createSelector(selectProjectFeature, ({ currentProjectID, allProjects }) => {
    return allProjects[currentProjectID]?.library?.[type]?.[varName] || {};
  });

export const selectNumberVariables = createSelector(
  selectProjectFeature,
  ({ currentProjectID, allProjects }) => {
    return allProjects[currentProjectID]?.library?.number || {};
  },
);

export const newProjectLoading = createSelector(
  selectProjectFeature,
  ({ newProjectLoading }): boolean => newProjectLoading,
);

export const selectProjectIsLoading = createSelector(
  selectProjectFeature,
  ({ error, isProjectLoaded }): boolean => !error && !isProjectLoaded,
);

export const isProjectLoaded = createSelector(
  selectProjectFeature,
  ({ isProjectLoaded }) => isProjectLoaded,
);

export const projectLoadError = createSelector(
  selectProjectFeature,
  ({ error }) => error,
);

export const isNewFileLoading = createSelector(
  selectProjectFeature,
  ({ newFileLoading }): boolean => newFileLoading,
);

export const selectMyScenes = (IRI: string) =>
  createSelector(selectProjectFeature, ({ files }) => {
    return Object.values(files).filter(
      file => (file.relationships?.childOf as ResourceData)?.IRI == IRI,
    );
  });

export const projectById = (id: string) =>
  createSelector(
    selectProjectFeature,
    ({ allProjects }: ProjectState): Project => {
      console.log(allProjects, id);
      return allProjects[id];
    },
  );

export const selectRecentProjets = createSelector(
  selectProjectFeature,
  ({ recentProjects }) => recentProjects,
);

export const selectExternalProjects = createSelector(
  selectProjectFeature,
  ({ externalProjects }) => externalProjects,
);

export const projectByOrganisationId = (id: string) =>
  createSelector(
    selectProjectFeature,
    ({ allProjects, newlyCreatedProjects }): Project[] =>
      Object.values(allProjects)
        .filter(project => project.organisationId === id)
        .sort((p1, p2) => (p1.name < p2.name ? -1 : 1))
        .sort(
          (p1, p2) =>
            (newlyCreatedProjects[p2.id] || 0) -
            (newlyCreatedProjects[p1.id] || 0),
        ),
  );

export const isDeletePendingById = (id: string) =>
  createSelector(
    selectProjectFeature,
    ({ deleteIsPending }): boolean => deleteIsPending == id,
  );

export const isDeletePending = createSelector(
  selectProjectFeature,
  ({ deleteIsPending }): string => deleteIsPending,
);

export const myRoleByProjectId = (id: string) =>
  createSelector(projectById(id), myUserId, (project, myUserId): RoleTypes => {
    console.log({
      project,
      myUserId,
    });
    return project?.roles[myUserId].type;
  });

export const getCurrentColorPalette = createSelector(
  selectProjectFeature,
  ({ currentProjectID, allProjects }) =>
    allProjects[currentProjectID]?.colorPalette || {},
);

export const getCurrentProjectField = <K extends keyof Project>(key: K) =>
  createSelector(
    selectProjectFeature,
    ({ currentProjectID, allProjects }) =>
      allProjects[currentProjectID]?.[key] as Project[K],
  );

export const getCurrentLanguage = createSelector(
  selectProjectFeature,
  ({ currentLanguage }) => currentLanguage,
);

export const getCurrentProjectLanguages = createSelector(
  selectProjectFeature,
  ({ currentProjectID, allProjects }) =>
    allProjects[currentProjectID]?.languages || [],
);

export const getCurrentProject = createSelector(
  selectProjectFeature,
  ({ currentProjectID, allProjects }) => allProjects[currentProjectID],
);

export const files = createSelector(
  selectProjectFeature,
  ({ currentProjectID, allProjects }) => {
    return allProjects[currentProjectID]?.files;
  },
);

export const STORYBOARD_TYPE_IRI = 'http://nowords.com#StoryBoardFile';

export const selectFileList = createSelector(
  selectProjectFeature,
  ({ files }) =>
    Object.values(files || {}).filter(
      ({ type, relationships }) =>
        !relationships.parent && !relationships.childOf,
      //  &&
      // type != STORYBOARD_TYPE_IRI,
    ),
);

export const selectScenes = createSelector(selectProjectFeature, ({ files }) =>
  Object.values(files || {}).filter(
    ({ relationships, literals }) =>
      !relationships.parent && !relationships.childOf,
    // &&
    // literals.sceneIndex !== undefined,
  ),
);

export const selectStoryBoardFile = createSelector(
  selectProjectFeature,
  ({ files }) =>
    Object.values(files || {}).filter(
      ({ type }) => type == STORYBOARD_TYPE_IRI,
    )?.[0],
);

export const selectCurrentBackgroundColor = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI }) => {
    const file = files[currentFileIRI];
    if (!file) {
      return;
    }

    const descriptor = file.literals.descriptor;
    const { backgroundColor } = descriptor || {};
    return backgroundColor || '#ffffff';
  },
);

export const selectCurrentFileID = createSelector(
  selectProjectFeature,
  ({ currentFileIRI }) => IRIToId(currentFileIRI),
);

export const selectCurrentFileIRI = createSelector(
  selectProjectFeature,
  ({ currentFileIRI }) => currentFileIRI,
);

export const selectCurrentFile = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI }) => files?.[currentFileIRI],
);

export const selectRootFilesBySceneIndex = createSelector(
  selectProjectFeature,
  ({ files }) => {
    const rootFiles = Object.values(files).filter(
      file => !file.relationships?.childOf,
    );

    // -- //
    return rootFiles.sort((a, b) =>
      asc(a.literals.sceneIndex, b.literals.sceneIndex),
    );
  },
);

export const selectCurrentScenes = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI: currentFileID }): ResourceData[] => {
    if (!currentFileID) {
      return [];
    }
    return Object.values(files)
      .filter(
        file =>
          (file.relationships?.childOf as ResourceData)?.IRI == currentFileID,
      )
      .map(file => files[file.IRI]);
  },
);

export const componentsList = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI: currentFileID }) => {
    return Object.keys(files[currentFileID].relationships.components || {})
      .filter(IRI => files[IRI])
      .map(IRI => files[IRI]);
  },
);

export const getCurrentProjectID = createSelector(
  selectProjectFeature,
  ({ currentProjectID }) => currentProjectID,
);

export const getProjectColorPalette = createSelector(
  selectProjectFeature,
  ({ currentProjectID, allProjects }) => {
    return Object.entries(allProjects[currentProjectID].colorPalette || {}).map(
      ([label, color]) => ({ label, color }),
    );
  },
);

export const selectFileByIRI = (IRI: string) =>
  createSelector(selectProjectFeature, state => state?.files[IRI]);

export const selectFiles = createSelector(
  selectProjectFeature,
  ({ files }) => files,
);

export const getCombinedColorPalette = createSelector(
  selectProjectFeature,
  selectOrganisationFeature,
  (
    { currentProjectID, allProjects },
    { allOrganisations, currentOrganisationID },
  ) => {
    const object = {
      ...(allOrganisations[currentOrganisationID]?.colorPalette || {}),
      ...(allProjects[currentProjectID]?.colorPalette || {}),
    };

    return Object.entries(object).map(([label, color]) => ({ label, color }));
  },
);

export const getCurrentFile = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI }) => files[currentFileIRI],
);

export const getCombinedColorPaletteObject = createSelector(
  selectProjectFeature,
  selectOrganisationFeature,
  (
    { currentProjectID, allProjects },
    { allOrganisations, currentOrganisationID },
  ) => {
    return {
      ...(allOrganisations[currentOrganisationID]?.colorPalette || {}),
      ...(allProjects[currentProjectID]?.colorPalette || {}),
    };
  },
);
export const canvasOrientation = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI }) =>
    files[currentFileIRI]?.literals?.descriptor?.orientation || 'landscape',
);

export const canvasBackgroundColor = createSelector(
  selectProjectFeature,
  ({ files, currentFileIRI }) =>
    files[currentFileIRI]?.literals?.descriptor?.backgroundColor || '#ffffff',
);

export const setFileDescriptorItem = createAction(
  'editor:set-file-descriptor-key',
  props<{ key: keyof GeneralShapeDescriptor; value: any }>(),
);
